import React, { useState, useEffect } from "react";
import { object, func, array, number } from "prop-types";
import { connect } from "react-redux";
import { get } from "lodash";

import SearchInput from "../../atoms/SearchInput";
import StoryList from "../../rows/story-list";
import AlsoRead from "../../rows/also-read";
import RecentSearch from "../../molecules/recent-search";
import SortFilterPopupView from "../../molecules/sort-filter-popup-view";

import { setLoading } from "../../helper/actions";
import { getAdvancedSearchStories, getWruStories } from "../../helper/api";
import { readCookie } from "../../helper/cookies";
import { addGTMPageVariables } from "../../utils/utils";

import "./search.m.css";

const NO_OF_ITEMS_LOAD = 8;

const SearchPageView = ({ data, setLoading, secondaryMenuItems, navigateToPage, merchantId }) => {
  const requiredParams = { q: data.query, sort: "latest-published" };
  data.isAuthor ? (requiredParams.author = data.query) : (requiredParams["tag-name"] = data.query);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [sortItem, setSortItem] = useState("latest-published");
  const [sectionIdList, setSectionList] = useState([]);
  const [stories, setStories] = useState(data.stories);
  const [alsoReadStories, setAlsoReadStories] = useState(null);
  const [offset, setOffset] = useState(data.stories.length);
  const [total, setTotal] = useState(data.total);
  const [authorStoriesTotal, setAuthorStoriesTotal] = useState(0);
  const [params, setParams] = useState(requiredParams);
  const [isMoreStories, setIsMoreStories] = useState(
    data.isAuthor ? data.stories.length < data.total : data.total > 8 ? "NA" : false
  );

  // Fetch also read stories only if there is no search stories
  useEffect(() => {
    if (!data.query || !total) {
      const thinmint = readCookie("thinmint");

      getWruStories(merchantId, thinmint, "qwsearch").then((response) => {
        const items = (response.length && response[0].recommendations) || [];
        setAlsoReadStories(items);
      });
    }

    addGTMPageVariables(null, "WEB", null, "SEARCH");

    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);

  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
  }, [isPopupOpen]);

  // If esc key pressed then close the popup
  const escFunction = (event) => {
    if (event.keyCode === 27) {
      setPopupOpen(false);
    }
  };

  const onSortFilterToggle = () => setPopupOpen(!isPopupOpen);

  async function updateSearchParams(type, value) {
    const paramObj = { ...params };

    if (type === "sort") {
      setSortItem(value);
      paramObj.sort = value;
    } else {
      setSectionList(value);

      if (value && value.length) {
        const sectionNameList = [];

        secondaryMenuItems.forEach((item) => {
          if (value.includes(item.id)) {
            sectionNameList.push(item["section-name"]);
          }
        });

        paramObj["section-name"] = sectionNameList.join();
      } else {
        delete paramObj["section-name"];
      }
    }

    setLoading(true);

    const authorParams = { ...paramObj, author: paramObj.q };
    delete authorParams["tag-name"];
    const tagParams = { ...paramObj, "tag-name": paramObj.q };
    delete tagParams.author;
    const authorCall = await getAdvancedSearchStories(0, NO_OF_ITEMS_LOAD, authorParams);
    const tagCall = await getAdvancedSearchStories(0, NO_OF_ITEMS_LOAD, tagParams);

    const stories = [].concat(authorCall.items.length > 0 ? authorCall.items : tagCall.items);
    const total = authorCall.total + tagCall.total;
    setParams(authorCall.total > 8 ? authorParams : tagParams);
    setTotal(total);
    setAuthorStoriesTotal(authorCall.total);
    setOffset(stories.length); // Keep next offset ready
    setStories(stories);
    setIsMoreStories(stories.length < total);
    setLoading(false);
  }

  // Load More stories
  const getStories = () => {
    setLoading(true);
    getAdvancedSearchStories(offset, NO_OF_ITEMS_LOAD, params)
      .then((result) => {
        const updatedResults = [].concat(stories, result.items);
        setAuthorStoriesTotal(updatedResults.length);
        setOffset(updatedResults.length < result.total ? updatedResults.length : 0); // Keep next offset ready
        setStories(updatedResults);
        // NA represents Non Author stories
        setIsMoreStories(updatedResults.length < result.total ? true : "NA"); // Decide which stories(author, tag) to load next
      })
      .finally(() => setLoading(false));
  };

  // Load More Tag stories
  const getTagStories = () => {
    setLoading(true);
    const tagParams = { ...params, "tag-name": params.q };
    delete tagParams.author;
    setParams(tagParams);
    getAdvancedSearchStories(offset, NO_OF_ITEMS_LOAD, tagParams)
      .then((result) => {
        const updatedResults = [].concat(stories, result.items);
        setTotal(authorStoriesTotal + result.total);
        setOffset(updatedResults.length - authorStoriesTotal); // Keep next offset ready
        setStories(updatedResults);
        setIsMoreStories(updatedResults.length < authorStoriesTotal + result.total ? "NA" : false);
      })
      .finally(() => setLoading(false));
  };

  const onRecentSearchItemClick = (text) => (e) => {
    const paramObj = { ...setParams };
    paramObj.q = text;

    setParams(paramObj);
    navigateToPage(`/search?q=${encodeURIComponent(text)}`);
  };

  // Display this view only if stories are present
  const getView = () => {
    if (!(stories && stories.length)) {
      return (
        <React.Fragment>
          <RecentSearch onItemClick={onRecentSearchItemClick} />
          <AlsoRead list={alsoReadStories} merchantId={merchantId} name="Search Page" />
        </React.Fragment>
      );
    }

    return (
      <div className="container">
        <div styleName="results-wrapper">
          <span styleName="total-count">Showing {total} results for</span>
          <span styleName="search-text">&quot;{data.query}&quot;</span>
        </div>

        <div styleName="sort-filter" onClick={onSortFilterToggle}>
          SORT / FILTER
        </div>

        <StoryList
          list={stories}
          onLoadMoreClick={isMoreStories === "NA" ? getTagStories : getStories}
          isMoreStories={isMoreStories}
        />

        {isPopupOpen ? (
          <div styleName="popup-wrapper">
            <div styleName="content">
              <SortFilterPopupView
                sections={secondaryMenuItems}
                closePopupClick={onSortFilterToggle}
                sortItem={sortItem}
                sectionIdList={sectionIdList}
                updateSearchParams={updateSearchParams}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div styleName="wrapper">
      <div styleName="search-wrapper">
        <SearchInput inputId="searchpage" isSearchIcon={false} defaultValue={params.q} />
      </div>

      {getView()}
    </div>
  );
};

SearchPageView.propTypes = {
  data: object,
  setLoading: func,
  secondaryMenuItems: array,
  navigateToPage: func,
  merchantId: number,
};

const mapStateToProps = (state) => ({
  secondaryMenuItems: get(state, ["qt", "data", "navigationMenu", "secondaryMenuItems"], []),
  merchantId: get(state, ["qt", "config", "publisher-attributes", "wru_id"]),
});

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
});

const SearchPage = connect(mapStateToProps, mapDispatchToProps)(SearchPageView);

export { SearchPage };
